import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SEOMeta from '../components/SEOMeta';

import { useAuth } from '../contexts/AuthContext';
import BottomNavBar from '../components/BottomNavBar';
import ParentalGate from '../components/ParentalGate';
import { Child } from '../types/child';

const logo = '/assets/images/logo.png';

/* ── Shared ─────────────────────────────────────────────────── */

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  display: flex;
  flex-direction: column;
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding-bottom: 96px;
`;

/* ─────────────────────────────────────────────────────────────
   GUEST (Not Logged In)
───────────────────────────────────────────────────────────── */

const GuestWrapper = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px 32px;
`;

const GuestCard = styled.div`
  width: 100%;
  background: white;
  border: 1px solid rgba(45,188,175,0.12);
  border-radius: 24px;
  padding: 36px 28px 32px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
`;

const GuestLogo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  img { height: 96px; width: auto; object-fit: contain; }
`;

const GuestTitle = styled.p`
  font-size: 18px;
  font-weight: 600;
  color: #475569;
  text-align: center;
  line-height: 1.5;
  margin: 0 0 28px 0;
`;

const GoogleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 56px;
  padding: 0 24px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #e2e8f0; }
`;

const GoogleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const Divider = styled.div`
  position: relative;
  padding: 20px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &::before {
    content: '';
    position: absolute;
    left: 0; right: 0;
    top: 50%;
    height: 1px;
    background: #e2e8f0;
  }
`;

const DividerLabel = styled.span`
  position: relative;
  background: white;
  padding: 0 12px;
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const FeatureCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: rgba(45,188,175,0.05);
  border: 1px solid rgba(45,188,175,0.12);
  border-radius: 16px;
  .material-symbols-outlined {
    font-size: 28px;
    color: #2DBCAF;
    margin-bottom: 8px;
  }
  span:last-child {
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    text-align: center;
    font-family: inherit;
  }
`;

const GuestFooter = styled.footer`
  margin-top: auto;
  padding: 20px 24px;
  background: #f8fafc;
  text-align: center;
  p {
    font-size: 13px;
    color: #94a3b8;
    margin: 0;
    font-family: inherit;
  }
`;

/* ─────────────────────────────────────────────────────────────
   LOGGED IN Profile
───────────────────────────────────────────────────────────── */

const ProfileMain = styled.main`
  flex: 1;
  overflow-y: auto;
`;

/* Avatar section */
const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 16px;
  gap: 4px;
`;

const AvatarCircle = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #2DBCAF;
  margin-bottom: 12px;
  background: #e2e8f0;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const AvatarInitials = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 3px solid #2DBCAF;
  background: linear-gradient(135deg, #2DBCAF, #059669);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 800;
  color: white;
  margin-bottom: 12px;
`;

const AvatarWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const EditAvatarBtn = styled.button`
  position: absolute;
  bottom: 8px;
  right: -4px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #2DBCAF;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  
  .material-symbols-outlined {
    font-size: 16px;
    color: white;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

/* Edit Profile Modal */
const EditOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const EditModal = styled.div`
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 400px;
  padding: 24px;
  animation: slideUp 0.2s ease-out;
  
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const EditTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 20px;
  text-align: center;
`;

const EditField = styled.div`
  margin-bottom: 16px;
`;

const EditLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
`;

const EditInput = styled.input<{ disabled?: boolean }>`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  background: ${p => p.disabled ? '#f1f5f9' : 'white'};
  color: ${p => p.disabled ? '#94a3b8' : '#1e293b'};
  
  &:focus {
    outline: none;
    border-color: #2DBCAF;
    box-shadow: 0 0 0 3px rgba(45,188,175,0.1);
  }
`;

const EditBtns = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const EditBtn = styled.button<{ primary?: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  
  ${p => p.primary ? `
    background: #2DBCAF;
    color: white;
    border: none;
    &:disabled {
      background: #94a3b8;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: #64748b;
    border: 1px solid #e2e8f0;
  `}
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const UserName = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  text-align: center;
  font-family: inherit;
`;

const UserEmail = styled.p`
  font-size: 15px;
  font-weight: 500;
  color: #64748b;
  margin: 0;
  text-align: center;
  font-family: inherit;
`;

const PremiumCrown = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #FFA929 0%, #FF8C00 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(255, 169, 41, 0.3);
  margin-bottom: 4px;
  
  .material-symbols-outlined {
    font-size: 16px;
    font-variation-settings: 'FILL' 1;
  }
`;

/* Premium Card */
const PremiumCard = styled.div`
  position: relative;
  overflow: hidden;
  margin: 8px 20px;
  border-radius: 20px;
  background: #FFA929;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(255,169,41,0.3);
`;

const PremiumDeco1 = styled.div`
  position: absolute;
  bottom: -24px; right: -24px;
  width: 128px; height: 128px;
  border-radius: 50%;
  background: rgba(255,255,255,0.1);
`;

const PremiumDeco2 = styled.div`
  position: absolute;
  top: -24px; left: -24px;
  width: 96px; height: 96px;
  border-radius: 50%;
  background: rgba(0,0,0,0.05);
`;

const PremiumInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const PremiumHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const PremiumTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  h2 {
    font-size: 20px;
    font-weight: 900;
    font-style: italic;
    color: #0f172a;
    text-transform: uppercase;
    letter-spacing: -0.5px;
    line-height: 1.1;
    margin: 0;
    font-family: inherit;
  }
  p {
    font-size: 13px;
    font-weight: 700;
    color: rgba(15,23,42,0.75);
    margin: 0;
    font-family: inherit;
  }
`;

const PremiumStarBtn = styled.div`
  width: 48px; height: 48px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined {
    font-size: 28px;
    color: white;
    font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }
`;

const PremiumList = styled.ul`
  list-style: none;
  padding: 0; margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PremiumListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  .material-symbols-outlined { font-size: 18px; }
`;

const PremiumBtn = styled.button`
  width: 100%;
  border-radius: 12px;
  background: #0f172a;
  padding: 16px;
  color: white;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 2px;
  text-transform: uppercase;
  border: none;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  transition: transform 0.1s;
  &:active { transform: scale(0.97); }
`;

/* Children Section */
const ChildrenSection = styled.div`
  padding: 0 20px 16px;
`;

const ChildrenHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  h3 {
    font-size: 16px;
    font-weight: 800;
    color: #0f172a;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const AddChildBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(45,188,175,0.1);
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #2DBCAF;
  font-family: inherit;
  cursor: pointer;
  .material-symbols-outlined { font-size: 16px; }
  &:hover { background: rgba(45,188,175,0.18); }
`;

const ChildCard = styled.div<{ $active?: boolean }>`
  background: ${p => p.$active ? 'rgba(45,188,175,0.08)' : 'white'};
  border: 2px solid ${p => p.$active ? '#2DBCAF' : '#e2e8f0'};
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  &:hover { border-color: ${p => p.$active ? '#2DBCAF' : '#cbd5e1'}; }
`;

const ActiveBadge = styled.span`
  background: #2DBCAF;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  margin-left: 6px;
`;

const ChildAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined { font-size: 24px; color: #f59e0b; }
`;

const ChildInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChildName = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 2px;
`;

const ChildMeta = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 0;
`;

const ChildActions = styled.div`
  display: flex;
  gap: 4px;
`;

const ChildActionBtn = styled.button<{ danger?: boolean }>`
  background: none;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${p => p.danger ? '#ef4444' : '#64748b'};
  &:hover { background: ${p => p.danger ? 'rgba(239,68,68,0.1)' : '#f1f5f9'}; }
  .material-symbols-outlined { font-size: 18px; }
`;

/* Child Modal */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 20px;
  padding: 24px;
  width: 100%;
  max-width: 380px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 20px;
  text-align: center;
`;

const ModalField = styled.div`
  margin-bottom: 14px;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 5px;
`;

const ModalInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  background: white;
  color: #0f172a;
  -webkit-appearance: none;
  &:focus { outline: none; border-color: #2DBCAF; }
  &[type="date"] {
    position: relative;
    &::-webkit-date-and-time-value {
      text-align: left;
    }
  }
  &::placeholder { color: #94a3b8; }
`;

const ModalRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const ModalBtns = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const ModalBtn = styled.button<{ primary?: boolean }>`
  flex: 1;
  height: 44px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: ${p => p.primary ? 'none' : '2px solid #e2e8f0'};
  background: ${p => p.primary ? '#2DBCAF' : 'white'};
  color: ${p => p.primary ? 'white' : '#64748b'};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

/* Menu List */
const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px;
`;

const MenuItem = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #f8fafc; }
`;

const MenuItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MenuIconBox = styled.div<{ danger?: boolean }>`
  width: 40px; height: 40px;
  border-radius: 50%;
  background: ${p => p.danger ? '#fee2e2' : 'rgba(45,188,175,0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined {
    font-size: 20px;
    color: ${p => p.danger ? '#ef4444' : '#2DBCAF'};
  }
`;

const MenuItemLabel = styled.span<{ danger?: boolean }>`
  font-size: 15px;
  font-weight: 600;
  color: ${p => p.danger ? '#ef4444' : '#0f172a'};
  font-family: inherit;
`;

/* Footer Links */
const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 16px;
  padding: 20px 16px 8px;
`;

const FooterLink = styled.a`
  font-size: 12px;
  color: #94a3b8;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    color: #64748b;
    text-decoration: underline;
  }
`;

const FooterCopyright = styled.p`
  font-size: 11px;
  color: #cbd5e1;
  text-align: center;
  margin: 8px 0 0;
  padding: 0 16px 16px;
`;

/* ── Component ─────────────────────────────────────────────── */

function formatAge(birthDate: string): string {
  const birth = new Date(birthDate);
  const now = new Date();
  const years = now.getFullYear() - birth.getFullYear();
  const months = now.getMonth() - birth.getMonth();
  const totalMonths = years * 12 + months;
  if (totalMonths < 12) return `${totalMonths} bulan`;
  const y = Math.floor(totalMonths / 12);
  const m = totalMonths % 12;
  return m > 0 ? `${y} tahun ${m} bulan` : `${y} tahun`;
}

export default function ProfilePage() {
  const { user, loading, logout, refetchUser, activeChild, setActiveChild } = useAuth();
  
  // Check if user has active premium
  const isPremium = user?.subscription?.status === 'active' && 
    user?.subscription?.endDate && 
    new Date(user.subscription.endDate) > new Date();
  const navigate = useNavigate();

  // Children state
  const [showChildModal, setShowChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState<Child | null>(null);
  const [childForm, setChildForm] = useState({ name: '', birthDate: '', height: '', weight: '' });
  const [childLoading, setChildLoading] = useState(false);
  
  // Edit profile states
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  
  // Parental gate state
  const [showParentalGate, setShowParentalGate] = useState(false);
  const [parentalVerified, setParentalVerified] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (editingChild) {
      setChildForm({
        name: editingChild.name,
        birthDate: editingChild.birthDate.split('T')[0],
        height: editingChild.height?.toString() ?? '',
        weight: editingChild.weight?.toString() ?? '',
      });
    } else {
      setChildForm({ name: '', birthDate: '', height: '', weight: '' });
    }
  }, [editingChild]);

  // Parental gate protection
  const requireParentalGate = useCallback((action: () => void) => {
    if (parentalVerified) {
      action();
    } else {
      setPendingAction(() => action);
      setShowParentalGate(true);
    }
  }, [parentalVerified]);

  const handleParentalSuccess = useCallback(() => {
    setShowParentalGate(false);
    setParentalVerified(true);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }, [pendingAction]);

  // Edit profile functions
  const openEditProfile = () => {
    setEditName(user?.name || '');
    setShowEditProfile(true);
  };

  const handleSaveProfile = async () => {
    if (!editName.trim() || editName.trim().length < 2) return;
    
    setEditLoading(true);
    try {
      const res = await fetch('/api/auth/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editName.trim() }),
      });
      
      if (res.ok) {
        await refetchUser();
        setShowEditProfile(false);
      }
    } catch (err) {
      console.error('Failed to update profile:', err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveChild = async () => {
    if (!childForm.name || !childForm.birthDate) return;
    setChildLoading(true);
    try {
      const url = editingChild ? `/api/children/${editingChild._id}` : '/api/children';
      const method = editingChild ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: childForm.name,
          birthDate: childForm.birthDate,
          height: childForm.height ? Number(childForm.height) : undefined,
          weight: childForm.weight ? Number(childForm.weight) : undefined,
        }),
      });
      await refetchUser();
      setShowChildModal(false);
      setEditingChild(null);
    } catch {}
    setChildLoading(false);
  };

  const handleDeleteChild = async (childId: string) => {
    if (!confirm('Hapus data anak ini?')) return;
    try {
      const res = await fetch(`/api/children/${childId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.ok) alert(data.error || 'Gagal menghapus');
      else await refetchUser();
    } catch {}
  };

  if (loading) return null;

  return (
    <>
      <SEOMeta
        title={user ? `Profil - ${user.name ?? 'Pengguna'}` : 'Login - Masuk ke KinderGame.id'}
        description={user
          ? 'Kelola akun, lihat favorit, dan pantau perkembangan belajar anak di KinderGame.id.'
          : 'Masuk ke KinderGame.id untuk mengakses game premium, menyimpan favorit, dan memantau perkembangan belajar anak.'}
        path="/profile"
      />
      <PageWrapper>
        {!user ? (
          /* ── GUEST ── */
          <>
            <GuestWrapper>
              <GuestCard>
                <GuestLogo>
                  <img src={logo} alt="Kindergame.id" />
                </GuestLogo>
                <GuestTitle>
                  Akses semua game premium<br />dengan satu akun
                </GuestTitle>
                <GoogleBtn onClick={() => { const returnTo = encodeURIComponent(window.location.pathname + window.location.search); window.location.href = '/api/auth/google?returnTo=' + returnTo; }}>
                  <GoogleIcon />
                  Masuk dengan Google
                </GoogleBtn>
                <Divider>
                  <DividerLabel>Platform game edukasi</DividerLabel>
                </Divider>
                <FeatureGrid>
                  <FeatureCard>
                    <span className="material-symbols-outlined">auto_awesome</span>
                    <span>Premium Game</span>
                  </FeatureCard>
                  <FeatureCard>
                    <span className="material-symbols-outlined">school</span>
                    <span>Ratusan Game Edukasi</span>
                  </FeatureCard>
                </FeatureGrid>
              </GuestCard>
            </GuestWrapper>
            <FooterLinks>
              <FooterLink onClick={() => navigate('/faq')}>FAQ</FooterLink>
              <FooterLink onClick={() => navigate('/refund-policy')}>Refund Policy</FooterLink>
              <FooterLink onClick={() => navigate('/terms')}>Syarat & Ketentuan</FooterLink>
              <FooterLink onClick={() => navigate('/contact')}>Kontak</FooterLink>
            </FooterLinks>
            <GuestFooter>
              <p>© 2024 KinderGame.id. Dibuat dengan cinta untuk anak-anak.</p>
            </GuestFooter>
          </>
        ) : (
          /* ── LOGGED IN ── */
          <ProfileMain>
            {/* Avatar + Info */}
            <AvatarSection>
              <AvatarWrapper>
                {user.avatar ? (
                  <AvatarCircle><img src={user.avatar} alt={user.name ?? ''} /></AvatarCircle>
                ) : (
                  <AvatarInitials>
                    {(user.name ?? 'U').charAt(0).toUpperCase()}
                  </AvatarInitials>
                )}
                <EditAvatarBtn onClick={openEditProfile}>
                  <span className="material-symbols-outlined">edit</span>
                </EditAvatarBtn>
              </AvatarWrapper>
              {isPremium && (
                <PremiumCrown>
                  <span className="material-symbols-outlined">crown</span>
                  Premium
                </PremiumCrown>
              )}
              <UserName>{user.name ?? 'Pengguna'}</UserName>
            </AvatarSection>

            {/* Children Section */}
            <ChildrenSection>
              <ChildrenHeader>
                <h3>
                  <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#2DBCAF' }}>child_care</span>
                  Data Anak
                </h3>
                <AddChildBtn onClick={() => { setEditingChild(null); setShowChildModal(true); }}>
                  <span className="material-symbols-outlined">add</span>
                  Tambah
                </AddChildBtn>
              </ChildrenHeader>

              {user.children && user.children.length > 0 ? (
                user.children.map(child => {
                  const isActive = activeChild?._id === child._id;
                  return (
                    <ChildCard 
                      key={child._id} 
                      $active={isActive}
                      onClick={() => !isActive && setActiveChild(child._id)}
                    >
                      <ChildAvatar>
                        <span className="material-symbols-outlined">face</span>
                      </ChildAvatar>
                      <ChildInfo>
                        <ChildName>
                          {child.name}
                          {isActive && <ActiveBadge>Aktif</ActiveBadge>}
                        </ChildName>
                        <ChildMeta>
                          {formatAge(child.birthDate)}
                          {child.height && ` • ${child.height} cm`}
                          {child.weight && ` • ${child.weight} kg`}
                        </ChildMeta>
                      </ChildInfo>
                      <ChildActions onClick={e => e.stopPropagation()}>
                        <ChildActionBtn onClick={() => requireParentalGate(() => { setEditingChild(child); setShowChildModal(true); })}>
                          <span className="material-symbols-outlined">edit</span>
                        </ChildActionBtn>
                        {(user.children?.length ?? 0) > 1 && (
                          <ChildActionBtn danger onClick={() => requireParentalGate(() => handleDeleteChild(child._id))}>
                            <span className="material-symbols-outlined">delete</span>
                          </ChildActionBtn>
                        )}
                      </ChildActions>
                    </ChildCard>
                  );
                })
              ) : (
                <ChildCard style={{ justifyContent: 'center', color: '#94a3b8', fontSize: 14 }}>
                  Belum ada data anak
                </ChildCard>
              )}
            </ChildrenSection>

            {/* Premium Card - hide for premium users */}
            {!isPremium && (
              <PremiumCard>
                <PremiumDeco1 />
                <PremiumDeco2 />
                <PremiumInner>
                  <PremiumHeader>
                    <PremiumTitleBlock>
                      <h2>Berlangganan<br />Premium</h2>
                      <p>Nikmati pengalaman bermain terbaik</p>
                    </PremiumTitleBlock>
                    <PremiumStarBtn>
                      <span className="material-symbols-outlined">stars</span>
                    </PremiumStarBtn>
                  </PremiumHeader>
                  <PremiumList>
                    <PremiumListItem>
                      <span className="material-symbols-outlined">check_circle</span>
                      Akses Semua Game
                    </PremiumListItem>
                    <PremiumListItem>
                      <span className="material-symbols-outlined">check_circle</span>
                      Bebas Iklan
                    </PremiumListItem>
                  </PremiumList>
                  <PremiumBtn onClick={() => navigate('/premium')}>Langganan Sekarang</PremiumBtn>
                </PremiumInner>
              </PremiumCard>
            )}

            {/* Menu */}
            <MenuList>
              <MenuItem as="div" onClick={() => navigate('/favorites')} style={{ cursor: 'pointer' }}>
                <MenuItemLeft>
                  <MenuIconBox>
                    <span className="material-symbols-outlined">favorite</span>
                  </MenuIconBox>
                  <MenuItemLabel>Daftar Favorit</MenuItemLabel>
                </MenuItemLeft>
                <span className="material-symbols-outlined" style={{ color: '#2DBCAF', fontSize: 20 }}>chevron_right</span>
              </MenuItem>

              <MenuItem as="div" onClick={() => navigate('/payments')} style={{ cursor: 'pointer' }}>
                <MenuItemLeft>
                  <MenuIconBox>
                    <span className="material-symbols-outlined">receipt_long</span>
                  </MenuIconBox>
                  <MenuItemLabel>Riwayat Pembayaran</MenuItemLabel>
                </MenuItemLeft>
                <span className="material-symbols-outlined" style={{ color: '#94a3b8', fontSize: 20 }}>chevron_right</span>
              </MenuItem>

              <MenuItem as="div" onClick={() => navigate('/privacy-policy')} style={{ cursor: 'pointer' }}>
                <MenuItemLeft>
                  <MenuIconBox>
                    <span className="material-symbols-outlined">policy</span>
                  </MenuIconBox>
                  <MenuItemLabel>Kebijakan Privasi</MenuItemLabel>
                </MenuItemLeft>
                <span className="material-symbols-outlined" style={{ color: '#94a3b8', fontSize: 20 }}>chevron_right</span>
              </MenuItem>

              <MenuItem as="button" style={{ width: '100%', background: 'white' }} onClick={logout}>
                <MenuItemLeft>
                  <MenuIconBox danger>
                    <span className="material-symbols-outlined">logout</span>
                  </MenuIconBox>
                  <MenuItemLabel danger>Keluar</MenuItemLabel>
                </MenuItemLeft>
              </MenuItem>
            </MenuList>

            {/* Footer Links */}
            <FooterLinks>
              <FooterLink onClick={() => navigate('/faq')}>FAQ</FooterLink>
              <FooterLink onClick={() => navigate('/refund-policy')}>Refund Policy</FooterLink>
              <FooterLink onClick={() => navigate('/terms')}>Syarat & Ketentuan</FooterLink>
              <FooterLink onClick={() => navigate('/contact')}>Kontak</FooterLink>
            </FooterLinks>
            <FooterCopyright>
              © 2024 KinderGame.id
            </FooterCopyright>
          </ProfileMain>
        )}
      </PageWrapper>

      <BottomNavBar />

      {/* Edit Profile Modal */}
      {showEditProfile && (
        <EditOverlay onClick={() => setShowEditProfile(false)}>
          <EditModal onClick={e => e.stopPropagation()}>
            <EditTitle>Edit Profil</EditTitle>
            <EditField>
              <EditLabel>Nama</EditLabel>
              <EditInput
                type="text"
                placeholder="Masukkan nama"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                autoFocus
              />
            </EditField>
            <EditField>
              <EditLabel>Email</EditLabel>
              <EditInput
                type="email"
                value={user?.email || ''}
                disabled
              />
            </EditField>
            <EditBtns>
              <EditBtn onClick={() => setShowEditProfile(false)}>Batal</EditBtn>
              <EditBtn 
                primary 
                onClick={handleSaveProfile}
                disabled={editLoading || !editName.trim() || editName.trim().length < 2}
              >
                {editLoading ? 'Menyimpan...' : 'Simpan'}
              </EditBtn>
            </EditBtns>
          </EditModal>
        </EditOverlay>
      )}

      {/* Child Add/Edit Modal */}
      {showChildModal && (
        <ModalOverlay onClick={() => { setShowChildModal(false); setEditingChild(null); }}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>{editingChild ? 'Edit Data Anak' : 'Tambah Anak'}</ModalTitle>
            <ModalField>
              <ModalLabel>Nama Anak *</ModalLabel>
              <ModalInput
                type="text"
                placeholder="Masukkan nama"
                value={childForm.name}
                onChange={e => setChildForm({ ...childForm, name: e.target.value })}
              />
            </ModalField>
            <ModalField>
              <ModalLabel>Tanggal Lahir *</ModalLabel>
              <ModalInput
                type="date"
                value={childForm.birthDate}
                onChange={e => setChildForm({ ...childForm, birthDate: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
              />
            </ModalField>
            <ModalRow>
              <ModalField>
                <ModalLabel>Tinggi (cm)</ModalLabel>
                <ModalInput
                  type="number"
                  placeholder="opsional"
                  value={childForm.height}
                  onChange={e => setChildForm({ ...childForm, height: e.target.value })}
                />
              </ModalField>
              <ModalField>
                <ModalLabel>Berat (kg)</ModalLabel>
                <ModalInput
                  type="number"
                  placeholder="opsional"
                  value={childForm.weight}
                  onChange={e => setChildForm({ ...childForm, weight: e.target.value })}
                />
              </ModalField>
            </ModalRow>
            <ModalBtns>
              <ModalBtn onClick={() => { setShowChildModal(false); setEditingChild(null); }}>Batal</ModalBtn>
              <ModalBtn
                primary
                onClick={handleSaveChild}
                disabled={childLoading || !childForm.name || !childForm.birthDate}
              >
                {childLoading ? 'Menyimpan...' : 'Simpan'}
              </ModalBtn>
            </ModalBtns>
          </ModalBox>
        </ModalOverlay>
      )}
      
      {showParentalGate && (
        <ParentalGate
          onSuccess={handleParentalSuccess}
          onCancel={() => {
            setShowParentalGate(false);
            setPendingAction(null);
          }}
          title="Verifikasi Orang Tua"
          subtitle="Untuk mengubah pengaturan, silakan verifikasi bahwa Anda adalah orang tua"
        />
      )}
    </>
  );
}
